import React, { useEffect, useCallback } from 'react';

export interface CancelSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  previousPlan: string;
  subscriptionEndDate?: number;
}

const CancelSuccessModal: React.FC<CancelSuccessModalProps> = ({
  isOpen,
  onClose,
  previousPlan,
  subscriptionEndDate,
}) => {
  const handleEscape = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleEscape]);

  const handleContinue = () => {
    onClose();
    // Force page reload to ensure all components get fresh subscription data
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  if (!isOpen) return null;

  // Format the end date if provided
  const formatEndDate = (timestamp?: number): string => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const endDateFormatted = formatEndDate(subscriptionEndDate);
  const hasEndDate = !!subscriptionEndDate && subscriptionEndDate > Date.now();

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cancel-success-title"
    >
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={handleContinue}
        aria-hidden="true"
      />

      <div className="relative z-10 w-full max-w-md bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden animate-fade-in">
        {/* Success Icon */}
        <div className="pt-8 pb-4 flex justify-center">
          <div className="w-20 h-20 bg-gradient-to-br from-blue-500/20 to-indigo-500/20 rounded-full flex items-center justify-center border-2 border-blue-500/30">
            <svg
              className="w-10 h-10 text-blue-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        </div>

        {/* Content */}
        <div className="px-8 pb-6 text-center">
          <h2 id="cancel-success-title" className="text-2xl font-bold text-white mb-3">
            Subscription Cancelled
          </h2>
          <p className="text-slate-300 mb-6">
            Your <span className="font-semibold text-white">{previousPlan}</span> subscription has been successfully cancelled.
            {hasEndDate ? (
              <>
                {' '}You'll continue to have access to all <span className="font-semibold text-white">{previousPlan}</span> features until <span className="font-semibold text-white">{endDateFormatted}</span>.
              </>
            ) : (
              <> Your account has been downgraded to the Free plan.</>
            )}
          </p>

          {/* Info Box */}
          <div className="bg-slate-800/50 rounded-xl p-4 mb-6 border border-slate-700">
            <div className="flex items-start gap-3 text-left">
              <svg
                className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                {hasEndDate ? (
                  <>
                    <p className="text-sm text-slate-300">
                      Your subscription will remain active until {endDateFormatted}.
                    </p>
                    <p className="text-sm text-slate-400 mt-2">
                      After that date, you'll be automatically moved to the Free plan.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-slate-300">
                      A confirmation email has been sent to your registered email address.
                    </p>
                    <p className="text-sm text-slate-400 mt-2">
                      You can resubscribe at any time from your account settings.
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* What's Next */}
          <div className="text-left mb-6">
            <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">
              What's Next
            </h3>
            <ul className="space-y-2">
              <li className="flex items-center gap-2 text-sm text-slate-300">
                <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Continue using free features
              </li>
              <li className="flex items-center gap-2 text-sm text-slate-300">
                <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Your saved sessions are preserved
              </li>
              <li className="flex items-center gap-2 text-sm text-slate-300">
                <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Upgrade anytime to restore premium access
              </li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-slate-800/50 border-t border-slate-700 px-8 py-4">
          <button
            onClick={handleContinue}
            className="w-full py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-xl font-semibold transition-all"
          >
            Continue to TradeRewind
          </button>
          <p className="text-center text-xs text-slate-500 mt-3">
            We're sorry to see you go. Thank you for being a customer!
          </p>
        </div>
      </div>
    </div>
  );
};

export default CancelSuccessModal;
